"use client";

import {
  captureBoundaryException,
  type SentryLike,
} from "@reopt/logger/sentry";

/**
 * 채팅 UI의 클라이언트 오류를 관측 파이프라인으로 보내는 통로.
 *
 * 🔑 **패키지가 Sentry SDK를 직접 import하지 않는다.** feature 패키지는 third-party
 * 의존성을 선언하지 않는 것이 규약이고(`packages/CLAUDE.md`), `@sentry/nextjs`를
 * 여기서 끌면 앱마다 다른 인스턴스가 resolve될 위험도 있다. 그래서 앱이 시작할 때
 * 인스턴스를 **한 번 주입**하고, 패키지는 주입된 것만 쓴다.
 *
 * ⚠️ 주입은 프롭이 아니라 모듈 레벨이다 — 바운더리는 `chat.tsx` **안쪽**에 있고
 * `<Chat>`을 렌더하는 호스트가 넷(스튜디오 패널·풀페이지·브랜드프론트 독·superadmin
 * 놀이터)이라, 프롭으로 만들면 호스트마다 배선을 복제하게 되고 반드시 한쪽이 빠진다.
 */

let sentry: SentryLike | null = null;

/** 앱 시작 시 한 번 호출한다(예: 클라이언트 instrumentation). `null`이면 해제. */
export function installChatErrorReporter(instance: SentryLike | null): void {
  sentry = instance;
}

/** 테스트·진단용 — 지금 보고가 실제로 나가는 상태인가. */
export function hasChatErrorReporter(): boolean {
  return sentry != null;
}

function readBrowserState():
  | { online: boolean; visibilityState: string }
  | undefined {
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return undefined;
  }
  return {
    online: navigator.onLine,
    visibilityState: document.visibilityState,
  };
}

/**
 * 채팅 에러 바운더리가 잡은 예외를 보고한다.
 *
 * 🔑 `Sentry.captureException`을 직접 부르지 않고 `captureBoundaryException`을 지난다 —
 * 그 함수만이 `digest`·`__NEXT_ERROR_CODE`를 태그로 실어 **서버 쪽 원인과 이어 준다**.
 *
 * ⚠️ 리포터가 설치되지 않은 환경(테스트·주입 전)에서도 **조용히 삼키지 않는다** —
 * 바운더리가 화면을 갈아치웠는데 아무 기록이 없으면 진단이 불가능해진다.
 */
export function reportChatBoundaryError(
  error: unknown,
  options: { boundary: string; componentStack?: string | null },
): void {
  if (!sentry) {
    console.error(
      `[chat-error-boundary] ${options.boundary} (리포터 미설치)`,
      error,
      options.componentStack ?? "",
    );
    return;
  }

  // ⚠️ componentStack은 여기 실리지 않는다 — `captureBoundaryException`의 계약에
  // 그 자리가 없다(태그로 넣기엔 크고, 우회하면 digest 태깅 규약이 두 벌이 된다).
  // 어느 컴포넌트가 던졌는지가 필요하면 logger 쪽 옵션을 여는 것이 정본 경로다.
  const browserState = readBrowserState();
  captureBoundaryException(sentry, error, {
    boundary: options.boundary,
    ...(browserState ? { browserState } : {}),
  });
}
